/* eslint-disable @typescript-eslint/no-explicit-any */
import axios, { AxiosInstance, AxiosRequestConfig } from 'axios';

import environment, { ApiAuthTypes } from '@/environment';
import { CoreQuery } from '@/common/services/core-query';
import { JWTLoginSessionManager } from '@/authentication/services/session/session-manager';

import devFileTransformers from './dev-file-transformers';
import { makeWarnLogger } from '@/common/services/debug-helper';

let axiosConfig: AxiosRequestConfig = {
    baseURL: environment.apiBaseUrl,
};

if (environment.apiAuthMode === ApiAuthTypes.key && environment.apiKey) {
    axiosConfig = {
        ...axiosConfig,
        headers: {
            apiKey: environment.apiKey,
        },
        ...(process.env.NODE_ENV === 'development' ? devFileTransformers : {}),
    };
}

const httpClient = axios.create(axiosConfig);

/** Creates authorized client from generated client factory function. */
export function authorizeClient<T>(
    factoryFunction: (configuration?: any, basePath?: string, axios?: AxiosInstance) => T
) {
    let apiBaseUrl = environment.apiBaseUrl || '';

    if (apiBaseUrl.endsWith('/')) {
        apiBaseUrl = apiBaseUrl.slice(0, apiBaseUrl.length - 1);
    }

    return factoryFunction(
        {
            apiKey: (keyName: string) => {
                if (keyName === 'apikey') return environment.apiKey;
                return '';
            },
            accessToken: async () => {
                /**
                 * Since session manager depends on `authorizeClient` via session refresher
                 * we have to call it as late as possible to overcome webpack circular dependency negative effects.
                 */
                const warnLog = makeWarnLogger('authorize-client');
                const sessionManager = new JWTLoginSessionManager();
                try {
                    return sessionManager.load().getToken();
                } catch {
                    warnLog("couldn't load token using session manager");
                }
            },
        },
        environment.apiBaseUrl,
        axios.create()
    );
}

export function initClientWithoutAuth<T>(
    factoryFunction: (configuration?: any, basePath?: string, axios?: AxiosInstance) => T
) {
    let apiBaseUrl = environment.apiBaseUrl || '';

    if (apiBaseUrl.endsWith('/')) {
        apiBaseUrl = apiBaseUrl.slice(0, apiBaseUrl.length - 1);
    }

    return factoryFunction({}, environment.apiBaseUrl, axios.create());
}

export const httpClientCoreQuery = {
    get: <T = any>(q: CoreQuery) => httpClient.get<T>(q.query, q.axiosConfigParams),
    post: <T = any>(q: CoreQuery, data?: any) => httpClient.post<T>(q.query, data, q.axiosConfigParams),
    put: <T = any>(q: CoreQuery, data?: any) => httpClient.put<T>(q.query, data, q.axiosConfigParams),
    patch: <T = any>(q: CoreQuery, data?: any) => httpClient.patch<T>(q.query, data, q.axiosConfigParams),
    delete: <T = any>(q: CoreQuery) => httpClient.delete<T>(q.query, q.axiosConfigParams),
};

export default httpClient;
